import { Component, Project } from "projen";

/**
 * Shape of an `owner/repo` slug. GitHub permits alphanumerics, `-`, `_`, and
 * `.` in both segments, separated by exactly one slash.
 */
const HOME_REPOSITORY_PATTERN = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/;

/**
 * Render the GitHub Actions expression fragment that pins a job to one
 * repository, validating the slug at synth time.
 *
 * Validation is deliberately eager: a typo'd slug matches no repository, so
 * every guarded job would skip silently while the aggregate `complete` gate
 * still reported success. Failing synth surfaces that immediately.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 * @param homeRepository - `owner/repo` slug, or `undefined` to emit no guard.
 * @param componentName - Component name used in the thrown error message.
 * @returns The condition fragment, or `undefined` when the workflow is unpinned.
 */
export const renderHomeRepositoryCondition = (
  homeRepository: string | undefined,
  componentName: string,
): string | undefined => {
  if (homeRepository === undefined) {
    return undefined;
  }

  if (!HOME_REPOSITORY_PATTERN.test(homeRepository)) {
    throw new Error(
      `${componentName} requires \`homeRepository\` to be an \`owner/repo\` slug, got "${homeRepository}"`,
    );
  }

  return `github.repository == '${homeRepository}'`;
};

/**
 * Inputs to {@link WorkflowHomeRepository.require}.
 */
export interface WorkflowHomeRepositoryOptions {
  /**
   * Workflow the pin applies to. Object identity is the key, so a projen
   * `BuildWorkflow` and a `GithubWorkflow` are both valid.
   */
  readonly workflow: Component;

  /**
   * Workflow file name, used in the thrown error message.
   */
  readonly workflowName: string;

  /**
   * `owner/repo` slug this component declared, or `undefined` for no pin.
   */
  readonly homeRepository?: string;

  /**
   * Component name used in the thrown error message.
   */
  readonly componentName: string;
}

/**
 * Render a slug for an error message, distinguishing "no pin" from a pin.
 */
const describePin = (homeRepository: string | undefined): string =>
  homeRepository === undefined ? "unset" : `"${homeRepository}"`;

/**
 * The `homeRepository` pin declared against one workflow file.
 *
 * The pin is a property of the workflow, not of whichever component declared
 * it: it guards every cloud-touching job in the file, and under the
 * `plan-apply` gate the shared `validate-plan` job carries it for every apply
 * job that needs it. A skipped `needs` job skips its dependents, so one
 * component's pin would otherwise silently govern services that never asked to
 * be pinned. This component records the first declaration and rejects any later
 * one that disagrees.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 */
export class WorkflowHomeRepository extends Component {
  public static of(
    project: Project,
    workflow: Component,
  ): WorkflowHomeRepository | undefined {
    const isDefined = (c: Component): c is WorkflowHomeRepository =>
      c instanceof WorkflowHomeRepository && c.workflow === workflow;
    return project.components.find(isDefined);
  }

  /**
   * Record a pin against a workflow, throwing when it disagrees with what an
   * earlier component declared for the same one.
   *
   * @param project - Project the record is attached to. Use the root project,
   * so components on sibling sub-projects find each other.
   */
  public static require(
    project: Project,
    options: WorkflowHomeRepositoryOptions,
  ): WorkflowHomeRepository {
    const { workflow, workflowName, homeRepository, componentName } = options;
    const existing = WorkflowHomeRepository.of(project, workflow);

    if (!existing) {
      return new WorkflowHomeRepository(project, options);
    }

    if (existing.homeRepository !== homeRepository) {
      throw new Error(
        `${componentName} requires every component sharing the workflow "${workflowName}" to declare the same \`homeRepository\`, got ${describePin(homeRepository)} here and ${describePin(existing.homeRepository)} already declared. The pin guards every job in one workflow file, so it cannot differ between them.`,
      );
    }

    return existing;
  }

  /**
   * Workflow this pin applies to.
   */
  public readonly workflow: Component;

  /**
   * `owner/repo` slug every component sharing the workflow must declare, or
   * `undefined` when the workflow is unpinned.
   */
  public readonly homeRepository?: string;

  constructor(project: Project, options: WorkflowHomeRepositoryOptions) {
    super(project);

    this.workflow = options.workflow;
    this.homeRepository = options.homeRepository;
  }
}
